import { useEffect } from 'react';
import { useServices } from '../../../app/services';
import type { SemanticModelSummary } from '../../services/semantic-model-catalog';
import { useAutosave } from '../../state/use-autosave';
import { useUiDispatch, useUiState } from '../../state/ui-state';
import { useWorkspace } from '../../state/workspace-context';
import { useWorkspaceShortcuts } from '../../state/use-workspace-shortcuts';
import { AiPanel } from '../ai/AiPanel';
import { AnalysisCanvas } from '../canvas/AnalysisCanvas';
import { WorkspaceDndProvider } from '../dnd/WorkspaceDndProvider';
import { FieldExplorer } from '../field-explorer/FieldExplorer';
import { PropertiesPanel } from '../properties/PropertiesPanel';
import { RejectionToast } from './RejectionToast';
import { TopBar } from './TopBar';

export interface WorkspaceShellProps {
  readonly models: readonly SemanticModelSummary[];
  readonly onModelChange: (modelId: string) => void;
}

/** Composicion del workspace: barra superior, explorador, canvas, propiedades y asistente. */
export function WorkspaceShell({ models, onModelChange }: WorkspaceShellProps) {
  const { document, lastCreatedCardId } = useWorkspace();
  const ui = useUiState();
  const uiDispatch = useUiDispatch();
  const { workspaceRepository } = useServices();
  const saveStatus = useAutosave(document, workspaceRepository);
  useWorkspaceShortcuts();

  useEffect(() => {
    if (lastCreatedCardId !== null) uiDispatch({ type: 'select-card', cardId: lastCreatedCardId });
  }, [lastCreatedCardId, uiDispatch]);

  return (
    <WorkspaceDndProvider>
      <div className="flex min-h-0 flex-1 flex-col">
        <TopBar
          models={models}
          saveStatus={saveStatus}
          onModelChange={(modelId) => {
            uiDispatch({ type: 'select-card', cardId: null });
            onModelChange(modelId);
          }}
        />
        <div className="flex min-h-0 flex-1">
          {ui.explorerOpen && <FieldExplorer />}
          <AnalysisCanvas />
          {ui.propertiesOpen && <PropertiesPanel />}
        </div>
      </div>
      <AiPanel />
      <RejectionToast />
    </WorkspaceDndProvider>
  );
}
